import type { StandardTextFieldProps } from '@mui/material';
import { InputAdornment, TextField } from '@mui/material';
import React, { useCallback, useEffect, type JSX } from 'react';
import { NumberInputStateless } from './FNumberInputStateless';
import type { NestedKeysToObject } from './FTextInput';
import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import { useLocale } from '@pflegenavi/frontend/localization';
import { stringMath } from './stringMath';

type StandardTextFieldPropsWithFilled = Omit<
  StandardTextFieldProps,
  'variant' | 'onChange'
> & {
  variant?: 'standard' | 'filled';
};

interface MoneyInputProps extends StandardTextFieldPropsWithFilled {
  allowNegative?: boolean;
  currencySymbol?: string;
  maxValue?: number;
  value?: number | null;
  onChange?: (value: number | undefined) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  allowMathExpressions?: boolean;
}

interface FMoneyInputProps<T extends string, V extends NestedKeysToObject<T>>
  extends Omit<MoneyInputProps, 'value'> {
  formik: FormikProps<V>;
  name: T;
  defaultValue?: T;
}

export const FMoneyInput = <
  Field extends string,
  V extends NestedKeysToObject<Field>
>({
  formik,
  name,
  defaultValue,
  onChange,
  ...props
}: FMoneyInputProps<Field, V>): JSX.Element => {
  const formikValue = getIn(formik.values, name);

  const { setFieldValue } = formik;
  const onChangeCallback = useCallback(
    (value: number | undefined) => {
      onChange ? onChange(value) : setFieldValue(name, value);
    },
    [onChange, setFieldValue, name]
  );

  return (
    <MoneyInput
      value={defaultValue ?? formikValue}
      onChange={onChangeCallback}
      disabled={formik.isSubmitting}
      onBlur={formik.handleBlur}
      name={name}
      helperText={getIn(formik.touched, name) && getIn(formik.errors, name)}
      error={Boolean(getIn(formik.errors, name) && getIn(formik.touched, name))}
      {...props}
    />
  );
};

const getEurosAndCents = (value?: number | null): string => {
  if (value === undefined || value === null) {
    return '';
  }
  const euros = Math.trunc(value / 100);
  const cents = Math.abs(value % 100)
    .toString()
    .padStart(2, '0');

  return `${euros}.${cents}`;
};

// Locale-aware display for the text-based math input (a number input
// localizes on its own and rejects comma values).
const formatEurosAndCents = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(value / 100);

const evaluateMathExpression = (expression: string): number | undefined => {
  try {
    const result = stringMath(expression.replace(/,/g, '.'));
    return isFinite(result) ? result : undefined;
  } catch {
    return undefined;
  }
};

export const MoneyInput: React.FC<MoneyInputProps> = ({
  slotProps,
  allowNegative = false,
  allowMathExpressions = false,
  currencySymbol = '€',
  maxValue = 99_999_99 /* ¢ */,
  value,
  onChange,
  onBlur: onBlurOriginal,
  ...props
}) => {
  const locale = useLocale();
  const formatValue = useCallback(
    (cents?: number | null): string =>
      cents == null
        ? ''
        : allowMathExpressions
        ? formatEurosAndCents(cents, locale)
        : getEurosAndCents(cents),
    [allowMathExpressions, locale]
  );

  const [stringValue, setStringValue] = React.useState<string | undefined>(
    value != null ? formatValue(value) : undefined
  );

  // Evaluates a (possibly partial) expression to cents, or undefined when it
  // is invalid or out of the allowed range.
  const expressionToCents = useCallback(
    (input: string): number | undefined => {
      const trimmed = input.trim();
      const expression = trimmed.startsWith('=') ? trimmed.slice(1) : trimmed;
      const evaluated = evaluateMathExpression(expression);
      if (evaluated === undefined) {
        return undefined;
      }
      const cents = Math.round(evaluated * 100);
      if (!allowNegative && cents < 0) {
        return undefined;
      }
      if (maxValue !== undefined && Math.abs(cents) > maxValue) {
        return undefined;
      }
      return cents;
    },
    [allowNegative, maxValue]
  );

  const originalRef = props.inputRef;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const usedRef = originalRef ?? inputRef;

  useEffect(() => {
    // Do not update the state while focused
    if (usedRef.current && usedRef.current.matches(':focus')) {
      return;
    }

    setStringValue((oldStringValue) => {
      // Special case: -0
      if (oldStringValue === '-0' && value === 0) {
        return `-0`;
      }

      return value != null ? formatValue(value) : undefined;
    });
  }, [value, usedRef, formatValue]);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventValue = event.target.value;

    if (eventValue === '') {
      setStringValue('');
      onChange && onChange(undefined);
      return;
    }

    if (allowMathExpressions) {
      setStringValue(eventValue);
      // Evaluate live so the parent sees "10+3,33" as 13.33 while typing.
      // Incomplete input like "10+" keeps the last valid value.
      const cents = expressionToCents(eventValue);
      if (cents !== undefined && cents !== value) {
        onChange?.(cents);
      }
      return;
    }

    const parsedValue = parseFloat(eventValue.trim());

    if (isNaN(parsedValue)) {
      onChange && onChange(undefined);
      return;
    }

    const centValue = Math.round(parsedValue * 100);

    if (maxValue !== undefined && Math.abs(centValue) > maxValue) {
      return;
    }

    // Disallow leading zeros
    if (/^-?0\d+/.test(eventValue)) {
      return;
    }

    setStringValue(eventValue);

    if (value === parsedValue) {
      // No change, don't update
      return;
    }

    onChange && onChange(centValue);
  };

  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (allowMathExpressions) {
        if ((stringValue?.trim() ?? '') === '') {
          setStringValue('');
          onChange?.(undefined);
          onBlurOriginal?.(event);
          return;
        }

        const cents = expressionToCents(stringValue ?? '');
        if (cents !== undefined) {
          setStringValue(formatValue(cents));
          if (cents !== value) {
            onChange?.(cents);
          }
          onBlurOriginal?.(event);
          return;
        }
      }

      setStringValue(formatValue(value));
      onBlurOriginal?.(event);
    },
    [
      allowMathExpressions,
      expressionToCents,
      formatValue,
      stringValue,
      onChange,
      onBlurOriginal,
      value,
    ]
  );

  const onFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const target = event.target;
    target.select();
  }, []);

  const inputSlotProps = {
    ...slotProps,
    input: {
      startAdornment:
        currencySymbol !== undefined ? (
          <InputAdornment disablePointerEvents position="start">
            {currencySymbol}
          </InputAdornment>
        ) : undefined,
      ...slotProps?.input,
    },
  };

  if (allowMathExpressions) {
    return (
      <TextField
        inputRef={usedRef}
        slotProps={inputSlotProps}
        onFocus={onFocus}
        value={stringValue ?? ''}
        onChange={onInputChange}
        onBlur={onBlur}
        {...props}
      />
    );
  }

  return (
    <NumberInputStateless
      inputRef={usedRef}
      allowNegative={allowNegative}
      allowDecimal={true}
      slotProps={inputSlotProps}
      onFocus={onFocus}
      value={stringValue ?? ''}
      onChange={onInputChange}
      onBlur={onBlur}
      {...props}
    />
  );
};
